import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Loader from './Loader'
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ----- Styled Components -----
// Reuse Loader, ModalOverlay, ModalContainer, ModalHeader, ModalField, Label, Input, ButtonRow, Button from AddStoreModal



const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 400px;
  max-width: 90%;
  padding: 30px 25px;
  box-shadow: 0 4px 20px rgba(35,62,88,0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #21325b;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 5px;
  color: #182237;
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  font-size: 1rem;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  color: #fff;
  background: ${props => props.variant === 'cancel' ? '#d24949' : '#426dff'};
  &:hover {
    background: ${props => props.variant === 'cancel' ? '#b33b3b' : '#305de2'};
  }
`;


// AddInventoryItemModal with category assignment
export default function AddInventoryItemModal({
  isOpen,
  onClose,
  onProductAdded,
  isEdit = false,
  editProduct = null,
  onProductUpdated
}) {
  const [formData, setFormData] = useState({ name: '', metric: '', category_id: '' });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Initialize form data on edit or reset on add/close
  useEffect(() => {
    if (isEdit && editProduct) {
      setFormData({
        name: editProduct.name || '',
        metric: editProduct.metric || '',
        category_id: editProduct.category_id || '',
      });
      setError('');
    } else {
      setFormData({ name: '', metric: '', category_id: '' });
      setError('');
    }
  }, [isEdit, editProduct, isOpen]);

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${BACKEND_URL}/categories`, { headers });
      if (res.status === 200) {
        setCategories(res.data);
      }
      
    } catch (err) {
      console.error("Error fetching categories", err.message);
      setError('Failed to load categories');
    }finally{
                setLoading(false)

    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));

    const errors = validateForm({ ...formData, [name]: value });
    setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
  };

  const validateForm = (fields = formData) => {
    let errors = {};

    // Regex: letters, spaces, apostrophes, dashes for name and metric are strict but customizable
    const nameRegex = /^[a-zA-Z0-9\s'-]{1,100}$/;
    const metricRegex = /^[a-zA-Z\s./-]{1,20}$/; // allows letters, spaces, dot, slash, dash

    if (!fields.name.trim()) {
      errors.name = "Name is required";
    } else if (!nameRegex.test(fields.name)) {
      errors.name = "Name contains invalid characters";
    }

    if (!fields.metric.trim()) {
      errors.metric = "Metric is required";
    } else if (!metricRegex.test(fields.metric)) {
      errors.metric = "Metric contains invalid characters";
    }

    if (!fields.category_id) {
      errors.category_id = "Category selection is required";
    }

    return errors;
  };


  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSubmitted(true);

    const errors = validateForm(formData);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      if (isEdit && editProduct) {
        const res = await axios.post(`${BACKEND_URL}/inventory/edit/${editProduct.id}`, formData, { headers });
        if (res.status === 200) {
          onProductUpdated && onProductUpdated();
          onClose();
        }
      } else {
        const res = await axios.post(`${BACKEND_URL}/inventory`, formData, { headers });
        if (res.status === 201) {
          setFormData({ name: '', metric: '', category_id: '' });
          onProductAdded && onProductAdded();
          onClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      {loading && <Loader />}
      <ModalContainer>
        <ModalHeader>{isEdit ? 'Edit Inventory Product' : 'Add New Inventory Product'}</ModalHeader>

        <ModalField>
          <Label>Name <span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
          <Input name="name" value={formData.name} onChange={handleChange} autoFocus />
          {(touched.name || submitted) && fieldErrors.name && (
            <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.name}</span>
          )}
        </ModalField>

        <ModalField>
          <Label>Metric <span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
          <Input name="metric" value={formData.metric} onChange={handleChange} />
          {(touched.metric || submitted) && fieldErrors.metric && (
            <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.metric}</span>
          )}
        </ModalField>

        <ModalField>
          <Label>Category <span style={{ color: 'red', marginLeft: '4px' }}>*</span></Label>
          <select
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            style={{ padding: '10px 12px', borderRadius: 8, fontSize: '1rem', border: '1px solid #dcdcdc' }}
          >
            <option value="">-- Select a Category --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.category_name}
              </option>
            ))}
          </select>
          {(touched.category_id || submitted) && fieldErrors.category_id && (
            <span style={{ color: '#d24949', fontSize: '0.95rem' }}>{fieldErrors.category_id}</span>
          )}
        </ModalField>

        {error && <div style={{ color: '#d24949', marginBottom: 10 }}>{error}</div>}
        <Label style={{ marginTop: '40px' }}>
          <span style={{ color: 'red', marginLeft: '4px' }}>*</span> marked fields are required.
        </Label>

        <ButtonRow>
          <Button variant="cancel" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Product' : 'Add Product')}
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}

